import { ReactLocalization, useLocalization } from "@fluent/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Row } from "@narmi/design_system";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { PageContent } from "@lending/components/PageContent";
import { PageHeader } from "@lending/components/PageHeader";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { PhoneNumberInput } from "@lending/components/PhoneNumberInput/PhoneNumberInput";
import { TextInput } from "@lending/components/TextInput/TextInput";
import { useListApplicationApplicantDetailsQuery } from "@lending/hooks/queries/ApplicationApplicantDetails/useListApplicationApplicantDetailsQuery";
import { useCurrentApplicationUuid } from "@lending/hooks/useCurrentApplicationUuid";
import { usePlaidLayer } from "@lending/hooks/usePlaidLayer";
import { useFlowNavigation } from "@lending/navigation";
import { InfoAlert } from "@lending/pages/Common/components/InfoAlert";
import { PREFILL_DATA_SOURCE } from "@lending/types";
import { findPrimaryApplicant } from "@lending/utils/applicantHelpers";
import { createValidatePhone } from "@lending/utils/validators";
import styles from "./PlaidPhoneNumber.module.css";
import { useStore } from "../../../../contexts/StoreContext";

function createSchema(l10n: ReactLocalization) {
  return z.object({
    phone: createValidatePhone(l10n),
    email: z
      .string()
      .email({ message: l10n.getString("error-invalid-email", null, "Enter a valid email") }),
  });
}

type FormData = z.infer<ReturnType<typeof createSchema>>;

function normalizedPhone(phone: string): string {
  return phone.replace(/\D/g, "").slice(-10);
}

function PlaidPhoneNumber() {
  const { l10n } = useLocalization();
  const { goNext } = useFlowNavigation();
  const { plaidHandler } = usePlaidLayer();
  const { data: storeData, updateData } = useStore();
  const schema = createSchema(l10n);
  const applicationUuid = useCurrentApplicationUuid();
  const { data: applicantResponse } = useListApplicationApplicantDetailsQuery();
  const primaryApplicant = findPrimaryApplicant(applicantResponse?.results);

  const { handleSubmit, control, reset } = useForm<FormData>({
    defaultValues: {
      phone: storeData.phone ?? "",
      email: storeData.email ?? "",
    },
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (!applicationUuid || !primaryApplicant) {
      return;
    }

    const phone = primaryApplicant.phone ?? "";
    const email = primaryApplicant.email ?? "";
    reset({ phone, email }, { keepDirtyValues: true });
    updateData({ phone, email });
  }, [applicationUuid, primaryApplicant, reset, updateData]);

  function onSkipThisStep() {
    updateData({
      skippedPlaidPrefill: true,
      phone: undefined,
      email: undefined,
      plaidPiiFormData: undefined,
      plaidPublicToken: undefined,
      prefillDataSource: PREFILL_DATA_SOURCE.MANUAL,
      // Drop the previous application's UUID. If a prior Plaid attempt
      // created an application, the manual flow must not re-use it.
      lendingApplicationID: undefined,
      // Clear PII captured from any prior attempt so the manual flow starts
      // from a clean slate.
      tax_id: undefined,
      date_of_birth: undefined,
    });
    goNext({ skippedPlaidPrefill: true, applicationUuid: undefined });
  }

  function onSubmit(data: FormData) {
    const phoneUnchanged = data.phone === storeData.phone;
    const matchesPersistedApplicant =
      primaryApplicant !== undefined &&
      normalizedPhone(data.phone) === normalizedPhone(primaryApplicant.phone ?? "") &&
      data.email === primaryApplicant.email;
    const phoneUnchangedForPrefill = phoneUnchanged || matchesPersistedApplicant;
    const hasPublicToken = Boolean(storeData.plaidPublicToken);
    const persistedPrefillSource = primaryApplicant?.prefill_data_source;
    const isPersistedManual = persistedPrefillSource === PREFILL_DATA_SOURCE.MANUAL;
    const isPersistedPlaid = persistedPrefillSource === PREFILL_DATA_SOURCE.PLAID_LAYER;

    updateData({
      phone: data.phone,
      email: data.email,
      skippedPlaidPrefill: phoneUnchangedForPrefill
        ? isPersistedManual || storeData.skippedPlaidPrefill
        : false,
    });

    if (phoneUnchanged && hasPublicToken) {
      goNext();
      return;
    }

    if (matchesPersistedApplicant && applicationUuid && (isPersistedManual || isPersistedPlaid)) {
      goNext({ skippedPlaidPrefill: isPersistedManual, applicationUuid });
      return;
    }

    if (plaidHandler) {
      plaidHandler.submit({ phone_number: data.phone, date_of_birth: null });
    }
  }

  return (
    <PageLayout>
      <PageContent>
        <div className={styles.phoneWrapper}>
          <PageHeader
            heading={l10n.getString("plaid-phone-number-heading", null, "Let\u2019s get started")}
            subheading=""
          />
          <div className={styles.inputRow}>
            <div className={styles.fieldsWrapper}>
              <PhoneNumberInput
                name="phone"
                label={l10n.getString("plaid-phone-number-label", null, "Phone number")}
                control={control}
              />
              <TextInput
                name="email"
                type="email"
                label={l10n.getString("form-input-email-label", null, "Email")}
                control={control}
              />
            </div>
          </div>

          <div className={styles.infoAlert}>
            <InfoAlert
              icon="lock"
              message={l10n.getString(
                "plaid-phone-number-alert",
                null,
                "We use Plaid to securely pre-fill your application."
              )}
            />
          </div>
          <Row alignItems="center" justifyContent="space-between">
            <button type="button" className={styles.fillManually} onClick={onSkipThisStep}>
              {l10n.getString("button-label-fill-manually", null, "Fill manually")}
            </button>
            <Button
              label={l10n.getString("button-label-next", null, "Next")}
              onClick={handleSubmit(onSubmit)}
            />
          </Row>
        </div>
      </PageContent>
    </PageLayout>
  );
}

export { PlaidPhoneNumber };
